import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { Icon } from '../components/Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M09PdfScreen'>;

export const M09PdfScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  
  // Customization options state
  const [includeLogo, setIncludeLogo] = useState(true);
  const [includeContact, setIncludeContact] = useState(true);
  const [includeDisclaimer, setIncludeDisclaimer] = useState(true);

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Document Preview Area */}
        <View style={styles.previewContainer}>
          <Text style={styles.previewHeader}>📄 XEM TRƯỚC TÀI LIỆU PROPOSAL</Text>
          
          <View style={styles.pdfPage}>
            {/* Mock PDF Content */}
            <View style={styles.pdfHeader}>
              <View style={styles.pdfBrand}>
                {includeLogo && <Icon name="calculate" size={24} color={colors.secondary} />}
                <Text style={styles.pdfTitle}>MANULIFE VIỆT NAM</Text>
              </View>
              <Text style={styles.pdfDocType}>BẢNG CHÀO GIÁ & MINH HỌA</Text>
            </View>

            <View style={styles.pdfDivider} />

            <View style={styles.pdfSection}>
              <Text style={styles.pdfSectionTitle}>Thông tin khách hàng</Text>
              <Text style={styles.pdfText}>Họ và tên: Nguyễn Văn Minh (35 tuổi, Nam)</Text>
              <Text style={styles.pdfText}>Sản phẩm chính: ManulifeMOVE - An Phát Đầu Tư</Text>
              <Text style={styles.pdfText}>Số tiền bảo hiểm: 1.000.000.000 VNĐ</Text>
              <Text style={styles.pdfText}>Phí bảo hiểm định kỳ: 30.000.000 VNĐ/năm</Text>
            </View>

            <View style={styles.pdfSection}>
              <Text style={styles.pdfSectionTitle}>Tóm tắt dòng tiền giả định (7%/năm)</Text>
              <Text style={styles.pdfText}>• Tổng phí đóng góp (20 năm): 600.000.000 VNĐ</Text>
              <Text style={styles.pdfText}>• Giá trị tài khoản tích lũy (Năm 20): 850.000.000 VNĐ</Text>
              <Text style={styles.pdfText}>• Quyền lợi tử vong rủi ro tối đa: 1.000.000.000 VNĐ</Text>
            </View>

            {includeContact && (
              <View style={styles.pdfAgent}>
                <Text style={styles.pdfAgentTitle}>Tư vấn viên hỗ trợ</Text>
                <Text style={styles.pdfText}>Họ tên: Nguyễn Hồng Minh</Text>
                <Text style={styles.pdfText}>Điện thoại: 0901 234 567</Text>
              </View>
            )}

            {includeDisclaimer && (
              <View style={styles.pdfDisclaimer}>
                <Text style={styles.pdfDisclaimerText}>
                  * Lưu ý: Các con số hiển thị phía trên mang tính chất minh họa giả định dựa trên lãi suất đầu tư quỹ. 
                  Quyền lợi thực tế sẽ thay đổi phụ thuộc vào kết quả kinh doanh thực tế.
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Customization Controls */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Tùy chọn hiển thị PDF</Text>
          
          <TouchableOpacity style={styles.optionRow} onPress={() => setIncludeLogo(!includeLogo)}>
            <Icon name={includeLogo ? 'check_circle' : 'circle'} size={20} color={includeLogo ? colors.secondary : '#9e9e9e'} />
            <Text style={styles.optionLabel}>Có logo thương hiệu</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.optionRow} onPress={() => setIncludeContact(!includeContact)}>
            <Icon name={includeContact ? 'check_circle' : 'circle'} size={20} color={includeContact ? colors.secondary : '#9e9e9e'} />
            <Text style={styles.optionLabel}>Có thông tin liên hệ đại lý</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.optionRow} onPress={() => setIncludeDisclaimer(!includeDisclaimer)}>
            <Icon name={includeDisclaimer ? 'check_circle' : 'circle'} size={20} color={includeDisclaimer ? colors.secondary : '#9e9e9e'} />
            <Text style={styles.optionLabel}>Hiển thị ghi chú miễn trừ trách nhiệm</Text>
          </TouchableOpacity>
        </View>

        {/* Download / Send Actions */}
        <View style={styles.actions}>
          <TouchableOpacity style={styles.actionBtnOutline} onPress={() => navigation.goBack()}>
            <Text style={styles.actionBtnTextOutline}>Quay lại</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionBtnSolid}>
            <Icon name="picture_as_pdf" size={20} color="#ffffff" />
            <Text style={styles.actionBtnTextSolid}>Tải PDF xuống</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  scrollContent: {
    padding: spacing[4],
    gap: spacing[4],
    paddingBottom: 40,
  },
  previewContainer: {
    gap: spacing[2],
  },
  previewHeader: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.onSurfaceVariant,
    paddingLeft: spacing[1],
  },
  pdfPage: {
    backgroundColor: '#ffffff',
    padding: spacing[5],
    borderRadius: borderRadius.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 10,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#e1e2e9',
    gap: spacing[4],
  },
  pdfHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pdfBrand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
  },
  pdfTitle: {
    fontSize: 12,
    fontWeight: '800',
    color: colors.primary,
    letterSpacing: 0.5,
  },
  pdfDocType: {
    fontSize: 10,
    fontWeight: '700',
    color: colors.onSurfaceVariant,
  },
  pdfDivider: {
    height: 1,
    backgroundColor: colors.primary,
  },
  pdfSection: {
    gap: spacing[1.5],
  },
  pdfSectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.primary,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
    paddingBottom: spacing[1],
  },
  pdfText: {
    fontSize: 11.5,
    color: colors.onSurface,
    lineHeight: 16,
  },
  pdfAgent: {
    backgroundColor: colors.surface,
    padding: spacing[3],
    borderRadius: borderRadius.sm,
    gap: spacing[1],
  },
  pdfAgentTitle: {
    fontSize: 11.5,
    fontWeight: '700',
    color: colors.secondary,
  },
  pdfDisclaimer: {
    marginTop: spacing[2],
  },
  pdfDisclaimerText: {
    fontSize: 10,
    color: colors.onSurfaceVariant,
    lineHeight: 14,
    fontStyle: 'italic',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    gap: spacing[3],
  },
  cardTitle: {
    fontSize: 14.5,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[1],
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
  },
  optionLabel: {
    fontSize: 13.5,
    color: colors.onSurface,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  actionBtnOutline: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
  },
  actionBtnTextOutline: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.primary,
  },
  actionBtnSolid: {
    flex: 2,
    flexDirection: 'row',
    backgroundColor: colors.primary,
    borderRadius: borderRadius.DEFAULT,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing[2],
  },
  actionBtnTextSolid: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
});
